import React, { useRef } from 'react';
import type { NavTab } from '../../config/navigation';

interface SubTabsProps {
  tabs: NavTab[];
  activeTab: string;
  onChange: (tabKey: string) => void;
  /** Names the tablist for screen readers, e.g. "Sales & Transactions views". */
  label: string;
}

/**
 * The sub-navigation was a row of plain buttons: nothing told assistive tech
 * they were a set, which one was current, or that arrow keys should move
 * between them. This is the same visual strip wired as a real tablist —
 * roving tabindex, Left/Right/Home/End, and `aria-selected` on the current tab.
 *
 * Panels stay owned by the section components, so `aria-controls` is left off
 * rather than pointed at an id that may not be in the DOM.
 */
const SubTabs: React.FC<SubTabsProps> = ({ tabs, activeTab, onChange, label }) => {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const focusTab = (index: number) => {
    const wrapped = (index + tabs.length) % tabs.length;
    tabRefs.current[wrapped]?.focus();
    onChange(tabs[wrapped].key);
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        focusTab(index + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        focusTab(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusTab(0);
        break;
      case 'End':
        event.preventDefault();
        focusTab(tabs.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      aria-orientation="horizontal"
      className="-mb-px flex gap-1 overflow-x-auto border-b border-border-subtle"
    >
      {tabs.map((tab, index) => {
        const isActive = tab.key === activeTab;
        return (
          <button
            key={tab.key}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={`subtab-${tab.key}`}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={`whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-blue ${
              isActive
                ? 'border-brand-blue text-brand-blue'
                : 'border-transparent text-ink-3 hover:border-border-strong hover:text-ink'
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default SubTabs;
